<script lang="ts" setup>
import { tpgIconArrowBack, tpgIconEdit, tpgIconFilePresent } from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import {
  computed,
  ref,
  toRefs,
  watch,
} from 'vue';

import { GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import {
  AvatarImage,
  bgPrimary,
  borderSeparatorTransparency,
  getIconOrIntitials,
  textSecondary,
  useSearchStore,
} from '@nora-monorepo/ui-shared';

import { useFilesModalStore } from '#nora-chat-ui/components/modals/files-modal/files-modal-store';
import { useConversationDetailsModal } from '#nora-chat-ui/components/modals/new-conversation/conversation-details-modal-context';
import type { ConversationState } from '#nora-chat-ui/states';
import { useAppStore } from '#nora-chat-ui/stores/app-store';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';

interface ConversationProps {
  conversationState: ConversationState;
}

const props = defineProps<ConversationProps>();

const { conversationState } = toRefs(props);
const icon = ref('');
const initials = ref('');

const searchStore = useSearchStore();

const filesModalStore = useFilesModalStore();
const appStore = useAppStore();
const userStore = useChatUserStore();
const conversationStore = useConversationStore();
const conversationDetailsModal = useConversationDetailsModal();

const conversation = computed(
  () => conversationState.value.conversationListResult,
);

watch(
  [conversation, userStore.user],
  () => {
    const { icon: newIcon, initials: newInitials } = getIconOrIntitials(
      conversation.value,
      userStore.user,
    );

    icon.value = newIcon ?? '';
    initials.value = newInitials ?? '';
  },
  { immediate: true },
);

const { getConversationTitle } = storeToRefs(conversationStore);
const conversationTitle = computed(() => {
  return getConversationTitle.value(conversationState.value.conversationListResult.id);
});

function editConversation(): Promise<void> {
  return conversationDetailsModal.openDetails(
    props.conversationState.conversationListResult.id,
  );
}

function openFileModal() {
  return filesModalStore.setConversationIdAndOpenModal(
    props.conversationState.conversationListResult.id,
  );
}

function closeConversation() {
  conversationStore.closeConversation(
    props.conversationState.conversationListResult.id,
  );
  searchStore.clearResults();
  appStore.toggleSidebarMobile(true);
}
</script>

<template>
  <div
    class="shrink-0 min-h-[48px] flex justify-between border-b"
    :class="[textSecondary, bgPrimary, borderSeparatorTransparency]"
  >
    <div class="flex items-center font-semibold min-w-0">
      <GrammarQuaternaryButton
        :icon-template="tpgIconArrowBack"
        @click="closeConversation"
      />
      <AvatarImage
        :id="conversationState.conversationListResult.id"
        :text="initials"
        :is-small="true"
        :icon="icon"
        :operation-id="conversationState.conversationListResult.operationId ?? ''"
        class="mx-4 shrink-0"
      />
      <span class="text-md whitespace-nowrap text-ellipsis overflow-hidden shrink" :title="conversationTitle">
        {{ conversationTitle }}
      </span>
      <GrammarQuaternaryButton
        v-chat-can="{
          permission: 'update:chat.conversation',
          conversationState,
        }"
        :icon-template="tpgIconEdit"
        @click="editConversation"
      />
    </div>
    <div class="flex items-center pr-2">
      <GrammarQuaternaryButton
        :icon-template="tpgIconFilePresent"
        @click="openFileModal"
      />
    </div>
  </div>
</template>

<style scoped></style>
